import React, { useEffect, useState, useLayoutEffect, useRef } from 'react'
import Modal from '~/Common-Objects/Modal'
import Button from '~/Common-Objects/UI/Button'
import Icon from '~/Common-Objects/UI/Icon'
import i18n from '~/i18n'
import { cn } from '~/utils/cn'
import { getOnboardingTooltipStyle } from '~/utils/onboardingTooltipPosition'

const OnboardingWizardLanding = function OnboardingWizardLanding({ onSkip = () => {} }) {
    const [currentStep, setCurrentStep] = useState(0)
    const steps = [
        {
            step: 1,
            id: 'dragdrop_lefttools',
            title: i18n.t('landingpage:wizard.add-content.title'),
            description: i18n.t('landingpage:wizard.add-content.description'),
            nextStep: i18n.t('landingpage:wizard.add-content.next'),
            action: () => {
                const stylesButton = document.querySelectorAll('.dndEditorv2-leftTools-tool')[1]
                if (stylesButton) {
                    stylesButton.click()
                }
            },
            orientation: 'right'
        },
        {
            step: 2,
            id: 'dragdrop_lefttools',
            title: i18n.t('landingpage:wizard.set-your-styles.title'),
            description: i18n.t('landingpage:wizard.set-your-styles.description'),
            nextStep: i18n.t('landingpage:wizard.set-your-styles.next'),
            action: () => {
                const checksButton = document.querySelectorAll('.dndEditorv2-leftTools-tool')[2]
                if (checksButton) {
                    checksButton.click()
                }
            },
            orientation: 'right'
        },
        {
            step: 3,
            id: 'dragdrop_mobile_desktop_toggle',
            title: i18n.t('landingpage:wizard.responsive-views.title'),
            description: i18n.t('landingpage:wizard.responsive-views.description'),
            nextStep: i18n.t('landingpage:wizard.responsive-views.next'),
            orientation: 'bottom'
        },
        {
            step: 4,
            id: 'dragdrop_preview_button',
            title: i18n.t('landingpage:wizard.previewing.title'),
            description: i18n.t('landingpage:wizard.previewing.description'),
            nextStep: i18n.t('landingpage:wizard.previewing.next'),
            orientation: 'bottom'
        }
    ]
    const [tooltipStyle, setTooltipStyle] = useState({})
    const [tooltipOffset, setTooltipOffset] = useState('center')
    const [finalOrientation, setFinalOrientation] = useState('top')
    const tooltipRef = useRef(null)
    const [measuredHeight, setMeasuredHeight] = useState(null)

    useEffect(() => {
        if (currentStep === 0) return

        const currentStepInfo = steps.find((step) => step.step === currentStep)
        if (!currentStepInfo) return

        document.querySelectorAll('[data-onboarding-show="true"]').forEach((element) => {
            element.setAttribute('data-onboarding-show', 'false')
        })

        document
            .querySelector(`[data-onboarding-id="${currentStepInfo.id}"]`)
            ?.setAttribute('data-onboarding-show', 'true')

        document.querySelectorAll('[data-onboarding-enabled="false"]').forEach((element) => {
            element.setAttribute('data-onboarding-enabled', 'true')
        })

        return () => {
            document.querySelectorAll('[data-onboarding-enabled="true"]').forEach((element) => {
                element.setAttribute('data-onboarding-enabled', 'false')
            })
        }
    }, [currentStep, steps])

    useLayoutEffect(() => {
        if (currentStep === 0) {
            setTooltipStyle((prev) => (Object.keys(prev).length === 0 ? prev : {}))
            return
        }
        const stepInfo = steps.find((step) => step.step === currentStep)

        if (!stepInfo) return

        const targetElement = document.querySelector(`[data-onboarding-id="${stepInfo.id}"]`)
        if (!targetElement) {
            setTooltipStyle((prev) => (Object.keys(prev).length === 0 ? prev : {}))
            return
        }
        const rect = targetElement.getBoundingClientRect()
        const tooltipWidth = 400
        const tooltipHeight = measuredHeight

        const {
            style,
            orientation: finalOrientation,
            arrowOffset
        } = getOnboardingTooltipStyle({
            rect,
            orientation: stepInfo.orientation,
            tooltipWidth,
            tooltipHeight,
            viewportWidth: window.innerWidth,
            viewportHeight: window.innerHeight
        })

        setTooltipStyle((prev) => {
            const keys = Object.keys(style)
            const changed = keys.some((k) => prev[k] !== style[k]) || Object.keys(prev).length !== keys.length
            return changed ? style : prev
        })
        setTooltipOffset(arrowOffset)
        setFinalOrientation(finalOrientation)
    }, [currentStep, steps, measuredHeight])

    useLayoutEffect(() => {
        if (tooltipRef.current) {
            const height = tooltipRef.current.offsetHeight
            if (height && height !== measuredHeight) {
                setMeasuredHeight(height)
            }
        }
    }, [tooltipStyle])

    const nextStep = () => {
        const stepInfo = steps.find((step) => step.step === currentStep)
        if (stepInfo && stepInfo.action) {
            stepInfo.action()
        }
        setCurrentStep(currentStep + 1)
    }

    const skipOnboarding = () => {
        setCurrentStep(99)
        onSkip()
    }

    const StepContent = () => {
        if (currentStep === 0) {
            return (
                <Modal
                    width='480px'
                    height='474px'
                    className='onboarding-start-modal'
                    noClickAway={true}
                    blurBg={true}
                    top={false}
                    closeButton={true}
                    onCloseButton={skipOnboarding}
                >
                    <img
                        src='https://cdn1.ourmailsender.com/siteContent/landingPages/landingpage-wizard-header-bg.png'
                        alt='Onboarding Wizard'
                        className='onboarding-start-modal__header'
                    />
                    <div className='onboarding-start-modal__content'>
                        <h2 className='onboarding-start-modal__title'>{i18n.t('landingpage:wizard.welcome.title')}</h2>
                        <p className='onboarding-start-modal__description'>
                            {i18n.t('landingpage:wizard.welcome.description')}
                        </p>
                        <div className='onboarding-start-modal__actions'>
                            <Button
                                variant='primary'
                                size='s'
                                iconRight={<Icon name='arrow-right' />}
                                onClick={() => setCurrentStep(1)}
                                postHogTag='drag-and-drop-onboarding-open-tour'
                            >
                                {i18n.t('landingpage:wizard.welcome.show-me-around')}
                            </Button>
                            <Button
                                variant='ghost'
                                size='s'
                                onClick={skipOnboarding}
                                postHogTag='drag-and-drop-onboarding-skip-tour'
                            >
                                {i18n.t('landingpage:wizard.welcome.skip')}
                            </Button>
                        </div>
                    </div>
                </Modal>
            )
        }

        if (currentStep === steps.length + 1) {
            return (
                <Modal
                    width='480px'
                    className='onboarding-start-modal'
                    noClickAway={true}
                    blurBg={true}
                    top={false}
                    closeButton={false}
                >
                    <div className='onboarding-start-modal__content'>
                        <h2 className='onboarding-start-modal__title'>{i18n.t('landingpage:wizard.finish.title')}</h2>
                        <p className='onboarding-start-modal__description'>
                            {i18n.t('landingpage:wizard.finish.description')}
                        </p>
                        <div className='onboarding-start-modal__actions'>
                            <Button
                                variant='primary'
                                size='s'
                                iconRight={<Icon name='arrow-right' />}
                                postHogTag='drag-and-drop-onboarding-complete-tour'
                                onClick={skipOnboarding}
                            >
                                {i18n.t('landingpage:wizard.finish.start-designing')}
                            </Button>
                        </div>
                    </div>
                </Modal>
            )
        }

        if (currentStep !== 0) {
            const stepInfo = steps.find((step) => step.step === currentStep)
            return (
                <div
                    ref={tooltipRef}
                    className={cn(
                        'onboarding-tooltip',
                        `onboarding-tooltip--${finalOrientation}` +
                            (tooltipOffset !== 'center' ? `-${tooltipOffset}` : '')
                    )}
                    style={tooltipStyle}
                >
                    <button
                        className='onboarding-tooltip__close'
                        data-ph='drag-and-drop-onboarding-skip-tour'
                        onClick={skipOnboarding}
                    >
                        <Icon name='cross-light' />
                    </button>
                    <h3 className='onboarding-tooltip__title'>{stepInfo.title}</h3>
                    <p className='onboarding-tooltip__description'>{stepInfo.description}</p>
                    <div className='onboarding-tooltip__actions'>
                        <span>{`${stepInfo.step} of ${steps.length}`}</span>
                        <Button variant='primary' size='s' onClick={nextStep} iconRight={<Icon name='arrow-right' />}>
                            {stepInfo.nextStep}
                        </Button>
                    </div>
                </div>
            )
        }
    }

    return (
        <div className='onboarding-wizard'>
            <StepContent />
        </div>
    )
}

export default React.memo(OnboardingWizardLanding)
